import type React from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatJst } from '../../lib/format.ts';
import type { ScoreRankingResponse, ScoreRankingRow } from '../../types/ranking.ts';
import { ZScoreBar } from './ZScoreBar.tsx';

type Props = { data: ScoreRankingResponse };

type Filter = 'all' | 'real_trend' | 'gap_chance';

type ParsedBreakdown = {
  z_score: { demand: number; supply: number; media: number };
  growth_rate: { demand: number; supply: number; media: number };
  sample_count: { demand: number; supply: number; media: number };
};

const ZERO_BREAKDOWN: ParsedBreakdown = {
  z_score: { demand: 0, supply: 0, media: 0 },
  growth_rate: { demand: 0, supply: 0, media: 0 },
  sample_count: { demand: 0, supply: 0, media: 0 },
};

const parseBreakdown = (raw: string | null | undefined): ParsedBreakdown => {
  if (!raw) return ZERO_BREAKDOWN;
  try {
    const parsed = JSON.parse(raw) as Partial<ParsedBreakdown>;
    return {
      z_score: { ...ZERO_BREAKDOWN.z_score, ...(parsed.z_score ?? {}) },
      growth_rate: { ...ZERO_BREAKDOWN.growth_rate, ...(parsed.growth_rate ?? {}) },
      sample_count: { ...ZERO_BREAKDOWN.sample_count, ...(parsed.sample_count ?? {}) },
    };
  } catch {
    return ZERO_BREAKDOWN;
  }
};

export const ScoreTable = ({ data }: Props): React.JSX.Element => {
  const [filter, setFilter] = useState<Filter>('all');

  const filtered = useMemo(() => {
    if (filter === 'real_trend') return data.rows.filter((r) => r.is_real_trend === 1);
    if (filter === 'gap_chance') return data.rows.filter((r) => r.is_gap_chance === 1);
    return data.rows;
  }, [data.rows, filter]);

  const realTrendCount = data.rows.filter((r) => r.is_real_trend === 1).length;
  const gapChanceCount = data.rows.filter((r) => r.is_gap_chance === 1).length;

  return (
    <>
      <div style={toolbarStyle}>
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
          全件 ({data.rows.length})
        </FilterChip>
        <FilterChip
          active={filter === 'real_trend'}
          onClick={() => setFilter('real_trend')}
          color="#27ae60"
        >
          本物のトレンド ({realTrendCount})
        </FilterChip>
        <FilterChip
          active={filter === 'gap_chance'}
          onClick={() => setFilter('gap_chance')}
          color="#e67e22"
        >
          ギャップ・参入チャンス ({gapChanceCount})
        </FilterChip>
      </div>
      <div style={legendStyle}>
        <strong>凡例:</strong>
        score = 0.4×需要z + 0.3×供給z + 0.3×メディアz / バー右(緑) = 業種平均より上 / バー左(赤) = 平均より下 / —
        = データなし
      </div>
      {filtered.length === 0 ? (
        <p style={mutedStyle}>該当する業種がありません。</p>
      ) : (
        <ul style={listStyle}>
          {filtered.map((row, idx) => (
            <ScoreRowItem key={row.industry_id} row={row} idx={idx} />
          ))}
        </ul>
      )}
      <small style={footerStyle}>
        算出: {formatJst(data.data_updated_at)} / algo {data.algo_version}
      </small>
    </>
  );
};

const ScoreRowItem = ({ row, idx }: { row: ScoreRankingRow; idx: number }): React.JSX.Element => {
  const breakdown = parseBreakdown(row.breakdown_json);
  return (
    <li style={rowStyle}>
      <div style={rowHeaderStyle}>
        <span style={rankStyle}>{idx + 1}</span>
        <Link to={`/industry/${row.industry_id}`} style={nameStyle}>
          {row.name}
        </Link>
        <span style={codeStyle}>{row.code}</span>
        <span style={scoreValueStyle}>score {row.score >= 0 ? '+' : ''}{row.score.toFixed(2)}</span>
        <span style={flagsStyle}>
          {row.is_real_trend === 1 && <span style={trendBadgeStyle}>本物のトレンド</span>}
          {row.is_gap_chance === 1 && <span style={gapBadgeStyle}>ギャップ</span>}
        </span>
      </div>
      <div style={barsStyle}>
        <ZScoreBar
          label="需要"
          value={breakdown.z_score.demand}
          sampleCount={breakdown.sample_count.demand}
        />
        <ZScoreBar
          label="供給"
          value={breakdown.z_score.supply}
          sampleCount={breakdown.sample_count.supply}
        />
        <ZScoreBar
          label="メディア"
          value={breakdown.z_score.media}
          sampleCount={breakdown.sample_count.media}
        />
      </div>
    </li>
  );
};

const FilterChip = ({
  active,
  onClick,
  color,
  children,
}: {
  active: boolean;
  onClick: () => void;
  color?: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    style={{
      ...chipStyle,
      ...(active
        ? {
            background: color ?? '#1976d2',
            color: '#fff',
            borderColor: color ?? '#1976d2',
          }
        : {}),
    }}
  >
    {children}
  </button>
);

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  padding: '10px 16px',
  borderBottom: '1px solid #f0f0f0',
  flexWrap: 'wrap',
};
const chipStyle: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: 12,
  background: '#fff',
  color: '#333',
  border: '1px solid #ccc',
  borderRadius: 999,
  cursor: 'pointer',
};
const legendStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 11,
  color: '#666',
  background: '#fafafa',
  borderBottom: '1px solid #f0f0f0',
  lineHeight: 1.5,
};
const listStyle: React.CSSProperties = { margin: 0, padding: 0, listStyle: 'none' };
const rowStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid #f0f0f0',
};
const rowHeaderStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '40px 1fr 200px 120px 220px',
  alignItems: 'center',
  marginBottom: 8,
};
const barsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 16,
  paddingLeft: 40,
  flexWrap: 'wrap',
};
const rankStyle: React.CSSProperties = { fontWeight: 700, fontSize: 14 };
const nameStyle: React.CSSProperties = { textDecoration: 'none', color: '#1976d2', fontSize: 14, fontWeight: 600 };
const codeStyle: React.CSSProperties = { color: '#777', fontSize: 12, fontFamily: 'monospace' };
const scoreValueStyle: React.CSSProperties = {
  color: '#333',
  fontSize: 13,
  textAlign: 'right',
  fontWeight: 600,
  fontFamily: 'monospace',
};
const flagsStyle: React.CSSProperties = { display: 'flex', gap: 6, justifyContent: 'flex-end' };
const trendBadgeStyle: React.CSSProperties = {
  background: '#27ae60',
  color: '#fff',
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
};
const gapBadgeStyle: React.CSSProperties = {
  background: '#e67e22',
  color: '#fff',
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
};
const mutedStyle: React.CSSProperties = { color: '#888', fontSize: 13, padding: '12px 16px' };
const footerStyle: React.CSSProperties = { color: '#888', fontSize: 13, padding: '12px 16px', display: 'block' };
